import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import { useNavigate } from 'react-router-dom'
import { PageLayout } from '$layouts/page'
import { usePage } from '$hooks/use-page'
import { Appointment } from '$api-client/types.gen'
import {
  appointmentDestroyMutation,
  appointmentIndexOptions,
  appointmentIndexQueryKey,
  appointmentUpdateMutation,
} from '$api-client/@tanstack/react-query.gen'
import {
  ChipCell,
  DateCell,
  DurationCell,
  Pagination,
  Text,
  ToastHelper,
} from '$components'
import { CheckDoneIcon, EyeIcon, XIcon } from '$components'
import { ElementIntent } from '$components'
import { ClientCell } from '$features/pets/components/cells/client-cell'
import { PetCell } from '$features/pets/components/cells/pet-cell'
import { GenericTable } from '$components'
import { OptionsCell } from '$components'
import { useModalState } from '$components'
import { ConfirmationModal } from '$components'
import { AppointmentRoutePath } from '../routes/protected'
import { usePageTitle } from '$components'
import { NoAppointmentRequestsEmptyState } from '../components/no-appointment-requests-empty-state'
import { TableSkeleton } from '$components'
import { PersonCell } from '$components'
import { useAuth } from '$features/auth/hooks/use-auth'
import { PermissionsHelper } from '$utils/permissions'
import { Role } from '$api-client/types.gen'
import { Router } from '$utils/router'
import dayjs from 'dayjs'
import { PetsFilterParam } from '$features/pets/enums/filter-params'
import { ClientsFilterParam } from '$features/clients/enums/filter-params'
import { AppointmentsFilterParam } from '../enums/filter-params'
import { QueryErrorHelper } from '$utils/query'

const columnHelper = createColumnHelper<Appointment>()

export function AppointmentRequestsPage() {
  usePageTitle('Appointment Requests')
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { page, setPage } = usePage()
  const { user } = useAuth()
  const { modalState: rejectModalState, close: closeRejectModal, open: openRejectModal } = useModalState()

  const { data: appointments, isLoading: isAppointmentsLoading } = useQuery({
    ...appointmentIndexOptions({
      query: {
        page,
        sort: ['start_at'],
        approved: 'pending',
      },
    }),
  })

  const { mutate: rejectAppointment, isPending: isRejectingAppointment } = useMutation({
    ...appointmentDestroyMutation(),
    onSuccess: () => {
      ToastHelper.success('Appointment request rejected.')
      queryClient.invalidateQueries({ queryKey: appointmentIndexQueryKey() })
      closeRejectModal()
    },
    onError: () => ToastHelper.error('Failed to reject appointment request.'),
  })

  const { mutate: approveAppointment } = useMutation({
    ...appointmentUpdateMutation(),
    onSuccess: () => {
      ToastHelper.success('Appointment approved.')
      queryClient.invalidateQueries({ queryKey: appointmentIndexQueryKey() })
    },
    onError: (error) => {
      const outdated = QueryErrorHelper.getQueryErrorByCode(error, 'outdated_appointment_request')

      ToastHelper.error(outdated ? outdated.message : 'Failed to approve appointment.')
    },
  })

  const handleRejectAppointment = () => {
    rejectAppointment({
      path: { id: rejectModalState.modelId! },
    })
  }

  const handleApproveAppointment = (appointmentId: number) => {
    approveAppointment({
      path: { id: appointmentId },
      body: {
        approved_at: dayjs().toISOString(),
      },
    })
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor('start_at', {
        header: 'Date',
        cell: ({ getValue }) => <DateCell date={getValue()} />,
      }),
      columnHelper.accessor('client', {
        header: 'Client',
        cell: ({ row }) => (
          <ClientCell
            data={row.original.client}
            query={!row.original.client.approved_at ? { [ClientsFilterParam.IncludeUnapproved]: '1' } : undefined}
          />
        ),
      }),
      columnHelper.accessor('pet', {
        header: 'Pet',
        cell: ({ row }) => (
          <PetCell
            data={row.original.pet}
            query={!row.original.pet.approved_at ? { [PetsFilterParam.IncludeUnapproved]: '1' } : undefined}
          />
        ),
      }),
      columnHelper.accessor('services', {
        header: 'Services',
        cell: ({ getValue }) => (
          <ChipCell values={getValue().map((service) => service.service_values.name)} />
        ),
      }),
      columnHelper.accessor('start_at', {
        id: 'duration',
        header: 'Duration',
        cell: ({ row }) => (
          <DurationCell
            startDate={row.original.start_at}
            endDate={row.original.finish_at}
          />
        ),
      }),
      columnHelper.accessor('groomer', {
        header: 'Groomer',
        cell: ({ row }) => <PersonCell data={row.original.groomer} />,
      }),
      columnHelper.display({
        id: 'actions',
        maxSize: 66,
        minSize: 66,
        cell: ({ row }) => (
          <OptionsCell
            options={[
              {
                id: 'view',
                value: 'View',
                icon: EyeIcon,
                onClick: () =>
                  navigate(
                    Router.getRoute(AppointmentRoutePath.AppointmentsView, { id: row.original.id }, !row.original.approved_at ? { [AppointmentsFilterParam.IncludeUnapproved]: '1' } : undefined),
                  ),
              },
              {
                id: 'approve',
                value: 'Approve',
                icon: CheckDoneIcon,
                onClick: () => handleApproveAppointment(row.original.id),
                disabled: PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN, Role.RECEPTIONIST])
                  || dayjs(row.original.start_at).isBefore(dayjs()),
                tooltip: PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN, Role.RECEPTIONIST])
                  ? 'You are not allowed to approve appointments.'
                  : dayjs(row.original.start_at).isBefore(dayjs())
                    ? 'Outdated appointment requests cannot be approved.'
                    : undefined,
              },
              {
                id: 'reject',
                value: 'Reject',
                intent: ElementIntent.Danger,
                onClick: () => openRejectModal(row.original.id),
                icon: XIcon,
              },
            ]}
          />
        ),
      }),
    ],
    [user.role, openRejectModal, navigate],
  )

  const data = useMemo(() => appointments?.data ?? [], [appointments])

  return (
    <PageLayout>
      <div className="grid grid-cols-1 grid-rows-[auto_1fr] h-full">
        <div className="pb-4 flex sm:flex-row flex-col sm:items-center justify-between gap-4 sm:gap-2">
          <div>
            <Text
              as="h1"
              weight="medium"
            >
              Appointment Requests
            </Text>

            <Text
              type="s"
              className="text-gray-500 mt-1"
            >
              When clients book online, their requests appear here for you to review and confirm.
            </Text>
          </div>
        </div>

        {isAppointmentsLoading && (
          <TableSkeleton
            columns={7}
            rows={10}
          />
        )}

        {!isAppointmentsLoading && data.length === 0 && <NoAppointmentRequestsEmptyState />}

        {data.length > 0 && (
          <>
            <GenericTable.Container>
              <GenericTable
                data={data}
                columns={columns}
                rowLink={(row) => Router.getRoute(
                  AppointmentRoutePath.AppointmentsView,
                  { id: row.id },
                  !row.approved_at ? { [AppointmentsFilterParam.IncludeUnapproved]: '1' } : undefined,
                )}
              />

              <Pagination
                currentPage={page}
                onChange={setPage}
                totalPages={appointments?.meta.last_page}
                totalItems={appointments?.meta.total}
                itemsPerPage={appointments?.meta.per_page}
                currentItemCount={appointments?.meta.current_page}
              />
            </GenericTable.Container>
          </>
        )}
      </div>

      {rejectModalState.isOpen && (
        <ConfirmationModal
          intent={ElementIntent.Danger}
          onClose={closeRejectModal}
          onConfirm={handleRejectAppointment}
          isLoading={isRejectingAppointment}
          title="Reject Appointment Request"
          description="Are you sure you want to reject this appointment request?"
        />
      )}
    </PageLayout>
  )
}
